import { cloneDeep, isEmpty } from 'lodash-es';
import React from 'react';

import { generateCountryGroupOptionsList } from '~/src/dash/api/country-group';
import { generateAudienceSegmentOptionsList } from '~/src/dash/apiV2/audienceSegments';
import { generateContextualSegmentOptionsList } from '~/src/dash/apiV2/contextualSegments';
import {
  DeviceOptions,
  DeviceORTBTypeOptions,
  generateCountryOptions,
  generateDeviceSubTypeOptionsList,
  ITargetingPreset,
  SamsungDeviceSubTypeOptions,
  TargetBoolOptions,
  TargetingCCPAOptions,
  TargetingCOPPAOptions,
  TargetingGDPRConsentOptions,
  TargetingGDPROptions,
  TargetingLiveStreamOptions,
  TargetingPresetKey,
  TargetingPresetKeys,
} from '~/src/dash/apiV2/targeting/targetingPresets';
import {
  generateOptionsListAsPromise,
  ISelectOption,
} from '~/src/dash/components/dropdowns/common';
import {
  convertSetIntoSingleNumber,
  convertSetIntoString,
  convertSingleNumberIntoArray,
  convertStringIntoArray,
  removeEmojiPrefixFromLabel,
} from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/common/common';
import { DaypartingBuilder } from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/DaypartingBuilder';
import {
  defaultDaypartingParsed,
  defaultDaypartingUI,
  emptyDaypartingDays,
  generateDaypartingPeriods,
  IDaypartingDays,
  IDaypartingParsed,
  IDaypartingUI,
} from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/DaypartingBuilder/common';
import { GDPRSelect } from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/GDPRSelect';
import {
  ILocalTargetingBuilderProps,
  LocalTargetingBuilder,
} from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/LocalTargetingBuilder';
import {
  defaultLocalTargetingUI,
  ILocalTargetingParsed,
  ILocalTargetingUI,
} from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/LocalTargetingBuilder/common';
import { RegionsEditor } from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/RegionsEditor';
import {
  ISelectWithIncludeAndLogicProps,
  SelectWithIncludeAndLogic,
} from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/SelectWithIncludeAndLogic';
import {
  ITextInputWithIncludeProps,
  TextInputWithInclude,
} from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/TextInputWithInclude';
import { IItemIDNameRoute } from '~/src/dash/components/tables/IDNameTable/common';
import { ISessionState } from '~/src/dash/reducers/session-reducers';
import { IUserState } from '~/src/dash/reducers/user-reducers';
import { ItemRouteType } from '~/src/dash/router/routes';
import { publicaColors } from '~/src/dash/utils/colors';
import { generateDMAOptions } from '~/src/dash/utils/countries';
import { DaypartingSummary } from '~/src/dash/views/tools/TargetingPresets/TargetingPresetFormView/TargetingSummaryTable/DaypartingSummary';
import { GDPRSummary } from '~/src/dash/views/tools/TargetingPresets/TargetingPresetFormView/TargetingSummaryTable/GDPRSummary';
import {
  IIncludeExcludeStringProps,
  IncludeExcludeString,
} from '~/src/dash/views/tools/TargetingPresets/TargetingPresetFormView/TargetingSummaryTable/IncludeExcludeString';
import { LocalTargetingSummary } from '~/src/dash/views/tools/TargetingPresets/TargetingPresetFormView/TargetingSummaryTable/LocalTargetingSummary';
import { SelectedOptionsWithLogic } from '~/src/dash/views/tools/TargetingPresets/TargetingPresetFormView/TargetingSummaryTable/SelectedOptionsWithLogic';

export type TargetingPresetSelectCategory =
  | 'Content'
  | 'Geo'
  | 'Time'
  | 'Privacy'
  | 'Custom'
  | 'Devices'
  | 'App'
  | 'Deals'
  | 'Streams'
  | 'Segments'
  | 'Site Pages'
  | 'Site Domains';

export type TargetingPresetCategory = 'Supply' | 'Users' | 'Customs';

export const TargetingPresetCategoryKeys: Record<string, TargetingPresetCategory> = {
  SUPPLY: 'Supply',
  USERS: 'Users',
  CUSTOMS: 'Customs',
};

export type TargetingPresetParamKey =
  // CONTENT
  | 'ContentChannel'
  | 'ContentDistributor'
  | 'ContentGenre'
  | 'ContentID'
  | 'ContentNetwork'
  | 'ContentProducer'
  | 'ContentRating'
  | 'ContentSeries'
  | 'ContentTitle'
  | 'ContentCategory'
  // GEO
  | 'TargetedRegions' // COUNTRY GROUPS
  | 'TargetedCountries'
  | 'LocalTargetingAllowed'
  | 'LocalTargetingDisallowed'
  // DAYPARTING
  | 'Dayparting'
  // PRIVACY
  | 'COPPA'
  | 'LimitedAdTracking'
  | 'CCPA'
  | 'GDPR'
  // CUSTOM
  | 'Custom4'
  | 'Custom5'
  | 'Custom6'
  | 'Custom7'
  | 'Custom8'
  | 'Custom9'
  | 'Custom10'
  | 'Custom11'
  | 'Custom12'
  | 'Custom13'
  | 'Custom14'
  | 'Custom15'
  | 'Custom16'
  | 'Custom17'
  | 'Custom18'
  | 'CustomTargeting'
  // DEVICE
  | 'TargetedDevices'
  | 'TargetedDeviceTypes'
  | 'TargetedDeviceSubtypes'
  | 'IPFilter'
  // APP
  | 'AppBundleIDs'
  // STREAMS
  | 'TargetedLivestreams'
  // SEGMENTS
  | 'AudienceSegments'
  | 'ContextualSegments'
  // SITE PAGES
  | 'SitePages'
  // SITE DOMAINS
  | 'SiteDomains';

export const TargetingPresetParamKeys: Record<string, TargetingPresetParamKey> = {
  // CONTENT
  CONTENT_CHANNEL: 'ContentChannel',
  CONTENT_DISTRIBUTOR: 'ContentDistributor',
  CONTENT_GENRE: 'ContentGenre',
  CONTENT_ID: 'ContentID',
  CONTENT_NETWORK: 'ContentNetwork',
  CONTENT_PRODUCER: 'ContentProducer',
  CONTENT_RATING: 'ContentRating',
  CONTENT_SERIES: 'ContentSeries',
  CONTENT_TITLE: 'ContentTitle',
  CONTENT_CATEGORY: 'ContentCategory',
  // GEO
  TARGETED_REGIONS: 'TargetedRegions', // COUNTRY GROUPS
  TARGETED_COUNTRIES: 'TargetedCountries',
  LOCAL_TARGETING_ALLOWED: 'LocalTargetingAllowed',
  LOCAL_TARGETING_DISALLOWED: 'LocalTargetingDisallowed',
  // DAYPARTING
  DAYPARTING: 'Dayparting',
  // PRIVACY
  COPPA: 'COPPA',
  LIMITED_AD_TRACKING: 'LimitedAdTracking',
  CCPA: 'CCPA',
  GDPR: 'GDPR',
  // CUSTOM
  CUSTOM_4: 'Custom4',
  CUSTOM_5: 'Custom5',
  CUSTOM_6: 'Custom6',
  CUSTOM_7: 'Custom7',
  CUSTOM_8: 'Custom8',
  CUSTOM_9: 'Custom9',
  CUSTOM_10: 'Custom10',
  CUSTOM_11: 'Custom11',
  CUSTOM_12: 'Custom12',
  CUSTOM_13: 'Custom13',
  CUSTOM_14: 'Custom14',
  CUSTOM_15: 'Custom15',
  CUSTOM_16: 'Custom16',
  CUSTOM_17: 'Custom17',
  CUSTOM_18: 'Custom18',
  CUSTOM_TARGETING: 'CustomTargeting',
  // DEVICE
  TARGETED_DEVICES: 'TargetedDevices',
  TARGETED_DEVICE_TYPES: 'TargetedDeviceTypes',
  TARGETED_DEVICE_SUBTYPES: 'TargetedDeviceSubtypes',
  IP_FILTER: 'IPFilter',
  // APP
  APP_BUNDLE_IDS: 'AppBundleIDs',
  // STREAMS
  TARGETED_LIVESTREAMS: 'TargetedLivestreams',
  // SEGMENTS
  AUDIENCE_SEGMENTS: 'AudienceSegments',
  CONTEXTUAL_SEGMENTS: 'ContextualSegments',
  // SITE PAGES
  SITE_PAGES: 'SitePages',
  // SITE DOMAINS
  SITE_DOMAINS: 'SiteDomains',
};

const generateGenericPlaceholder = (label: string) => {
  return `Enter ${label} here, comma separated.`;
};

const generateContentPlaceholder = (label: string) => {
  return `Enter ${label} to be bid on here, comma separated.`;
};

const generateCustomPlaceholder = (label: string) => {
  return `Enter ${label} here. Can be comma separated.`;
};

const customTooltipMessage = `Checks for custom parameters set in your request to Publica's endpoint and includes or excludes the bidder. Can be comma separated.`;

const AppBundleTooltipMessage = (
  <div>
    App Bundle ID to bid on
    <br />- Android: com.hulu.plus
    <br />- Apple TV: 482066631
    <br />- Roku: 12
    <br />- Fire TV: B004U7BXBO
    <br />- Samsung: G15115002089
    <br /> More Info{' '}
    <a
      href='https://iabtechlab.com/wp-content/uploads/2020/08/IAB-Tech-Lab-OTT-store-assigned-App-Identification-Guidelines-2020.pdf'
      rel='noopener'
      target='_blank'
    >
      Here
    </a>
  </div>
);

const convertDevicesSelectedValue = (value: Set<number>) => {
  if (value.size === DeviceOptions.length) {
    return '0';
  } else {
    return Array.from(value).join(',');
  }
};

const convertDevicesResponseValue = (value: string) => {
  switch (true) {
    case !value:
    case value === '0':
      return DeviceOptions.map((option) => option.id);
    default:
      return value.split(',').map(Number);
  }
};

export const convertSingleNumericIDForSummaryZeroDefault = (
  value: number,
  options: ISelectOption[],
) => {
  if (!value) return '';
  const selectedOption = options.find((option) => option.id === value);
  if (selectedOption && !isEmpty(selectedOption)) {
    return selectedOption.label;
  }
  return '';
};

const convertNumericIDsForSummary = (value: string, options: ISelectOption[]) => {
  if (!value) return '';
  const selectedIDs = new Set(value.split(',').map((v) => Number(v)));
  if (selectedIDs.size === options.length) return '';
  const selectedLabels: string[] = [];
  options.forEach(({ id, label }) => {
    if (selectedIDs.has(id as number)) {
      selectedLabels.push(label);
    }
  });
  return selectedLabels.join(', ');
};

const convertNumericIDsForSummaryZeroAll = (value: string, options: ISelectOption[]) => {
  if (!value || value === '0') return '';
  const selectedIDs = new Set(value.split(',').map((v) => Number(v)));
  if (selectedIDs.size === options.length) return '';
  const selectedLabels: string[] = [];
  options.forEach(({ id, label }) => {
    if (selectedIDs.has(id as number)) {
      selectedLabels.push(label);
    }
  });
  return selectedLabels.join(', ');
};

const convertCountryIDsForSummary = (value: string, options: ISelectOption[]) => {
  if (!value) return '';
  const selectedIDs = new Set(value.split(',').map((v) => v.toLowerCase()));
  const selectedLabels: string[] = [];
  options.forEach((option) => {
    if (selectedIDs.has(option.id as string)) {
      selectedLabels.push(removeEmojiPrefixFromLabel(option));
    }
  });
  return selectedLabels.join(', ');
};

const convertIDNameForSummary = (value: IItemIDNameRoute[]) => {
  if (!value || isEmpty(value)) return '';
  const selectedLabels: string[] = [];
  value.forEach((v) => {
    selectedLabels.push(v.Name);
  });
  return selectedLabels.join(', ');
};

const convertDeviceTypesSelectedValue = (value: Set<number>) => {
  if (value.size === DeviceORTBTypeOptions.length) {
    return '';
  } else {
    return Array.from(value).join(',');
  }
};

const convertDeviceTypesResponseValue = (value: string) => {
  switch (true) {
    case !value:
      return DeviceORTBTypeOptions.map((option) => option.id);
    default:
      return value.split(',').map(Number);
  }
};

const convertDeviceSubTypesSelectedValue = (value: Set<number>) => {
  const size = value.size;
  if (size === 0 || size === SamsungDeviceSubTypeOptions.length) {
    return '0';
  } else {
    return Array.from(value).join(',');
  }
};

const convertDeviceSubTypesResponseValue = (value: string) => {
  switch (true) {
    case !value:
    case value === '0':
      return SamsungDeviceSubTypeOptions.map((option) => option.id);
    default:
      return value.split(',').map(Number);
  }
};

const convertLivestreamResponseValue = (value: string) => {
  if (!value || value === '0') {
    return TargetingLiveStreamOptions.map((option) => option.id);
  } else {
    return value.split(',').map(Number);
  }
};

const convertLivestreamSelectedValue = (value: Set<number>) => {
  if (value.size === TargetingLiveStreamOptions.length) {
    return '0';
  } else {
    return Array.from(value).join(',');
  }
};

const parseLocalTargetingForUI = (localTargetingBlob: string) => {
  const localTargeting: ILocalTargetingParsed[] = !!localTargetingBlob
    ? JSON.parse(localTargetingBlob)
    : [];

  const localTargetingUI: ILocalTargetingUI[] = [];
  let reactKey = 1001;
  localTargeting.forEach((lt) => {
    localTargetingUI.push({
      type: lt.type || 'country',
      country: lt.country || '',
      states: lt.states || '',
      areStatesCommaSeparated: !!lt.areStatesCommaSeparated,
      cities: lt.cities || '',
      areCitiesCommaSeparated: !!lt.areCitiesCommaSeparated,
      zips: lt.zips || '',
      dmas: lt.dmas || '',
      areDMAsCommaSeparated: !!lt.areDMAsCommaSeparated,
      reactKey,
    });
    reactKey++;
  });

  if (isEmpty(localTargetingUI)) {
    localTargetingUI.push(cloneDeep(defaultLocalTargetingUI));
    localTargetingUI[0].reactKey = reactKey;
  }

  return localTargetingUI;
};

const convertStringToTrimmedArray = (value: string) => {
  if (!value) return [];
  return value.split(',').map((s) => s.trim());
};

const checkForDuplicates = (array: string[]) => {
  return new Set(array).size !== array.length;
};

const convertLocalTargetingUI = (locations: ILocalTargetingUI[]) => {
  const result: ILocalTargetingParsed[] = [];
  const errors: Set<string> = new Set([]);
  locations.forEach((loc) => {
    if (loc.country) {
      // validate
      // states
      const states = convertStringToTrimmedArray(loc.states);
      if (checkForDuplicates(states)) {
        errors.add('Please remove duplicate state values.');
      }
      // cities
      const cities = convertStringToTrimmedArray(loc.cities);
      if (checkForDuplicates(cities)) {
        errors.add('Please remove duplicate city values.');
      }
      // zips
      const zips = convertStringToTrimmedArray(loc.zips);
      if (zips.some((zip) => zip.length < 3 && zip !== Number(zip).toString())) {
        errors.add('Invalid zip codes found in Local Targeting.');
      }
      if (checkForDuplicates(zips)) {
        errors.add('Please remove duplicate zip codes.');
      }
      // dmas
      const dmas = convertStringToTrimmedArray(loc.dmas);
      if (checkForDuplicates(dmas)) {
        errors.add('Please remove duplicate DMAs.');
      }

      result.push({
        type: loc.type,
        country: loc.country,
        states: states.join(','),
        areStatesCommaSeparated: !!loc.areStatesCommaSeparated,
        cities: cities.join(','),
        areCitiesCommaSeparated: !!loc.areCitiesCommaSeparated,
        zips: zips.join(','),
        dmas: dmas.join(','),
        areDMAsCommaSeparated: !!loc.areDMAsCommaSeparated,
      });
    }
  });
  return {
    value: JSON.stringify(result),
    errors: Array.from(errors),
  };
};

const parseDaypartingForUI = (daypartingBlob: string, session: ISessionState): IDaypartingUI => {
  const dayparting: IDaypartingParsed = !!daypartingBlob ? JSON.parse(daypartingBlob) : {};

  const daypartingUI = cloneDeep(defaultDaypartingUI);

  daypartingUI.Timezone = dayparting?.Timezone || session.publisher.Timezone;
  daypartingUI.Enabled = dayparting?.Enabled || false;
  daypartingUI.EnableViewerTimezone = dayparting?.EnableViewerTimezone || false;

  const schedule = dayparting?.Schedule || defaultDaypartingParsed.Schedule;
  daypartingUI.Periods = generateDaypartingPeriods(schedule);

  return daypartingUI;
};

const convertDaypartingUI = (props: IDaypartingUI) => {
  const errors: string[] = [];
  const { Periods, EnableViewerTimezone, Enabled, Timezone } = props;
  const daypartingDays = cloneDeep(emptyDaypartingDays);
  const dayKeys = Object.keys(daypartingDays);

  if (Periods.length > 0) {
    Object.values(Periods).forEach((period) => {
      period.days.forEach((day: keyof IDaypartingDays) => {
        daypartingDays[day].push([period.start, period.end]);
      });
    });
  }

  // sort intervals
  dayKeys.forEach((dayKey: keyof IDaypartingDays) => {
    daypartingDays[dayKey] = daypartingDays[dayKey].sort((a, b) => {
      if (a[0].isBefore(b[0])) {
        return -1;
      } else if (a[0].isAfter(b[0])) {
        return 1;
      }
      return 0;
    });
  });

  // merge intervals
  dayKeys.forEach((dayKey: keyof IDaypartingDays) => {
    const results = [] as [moment.Moment, moment.Moment][];
    daypartingDays[dayKey].forEach((interval) => {
      const prev = results[results.length - 1];
      if (!results.length || prev[1].isBefore(interval[0])) {
        results.push(interval);
      } else {
        if (prev[1].isBefore(interval[1])) {
          prev[1] = interval[1];
        }
      }
      daypartingDays[dayKey] = results;
    });
  });

  const emptyDayparting = cloneDeep(defaultDaypartingParsed);

  // convert moments to string
  dayKeys.forEach((dayKey: keyof IDaypartingDays) => {
    daypartingDays[dayKey].forEach((interval) => {
      emptyDayparting.Schedule[dayKey].push([
        interval[0].format('HH:mm'),
        interval[1].format('HH:mm'),
      ]);
    });
  });

  // populate rest for conversion
  emptyDayparting.EnableViewerTimezone = EnableViewerTimezone;
  emptyDayparting.Enabled = Enabled;
  emptyDayparting.Timezone = Timezone;

  return {
    value: JSON.stringify(emptyDayparting),
    errors,
  };
};

const validateTextInput = (value: string, parameterLabel: string) => {
  if (!value) {
    return [`${parameterLabel} is empty.`];
  }
  return [];
};

interface IConvertedPayload {
  value: any;
  errors: string[];
}

interface ICustomPresetConversion {
  convertResponse: (value: any, session: ISessionState) => any;
  convertPayload: (value: any) => IConvertedPayload;
  uiKey: TargetingPresetKey;
  apiKey: TargetingPresetKey;
}

interface ICustomValidation {
  validateKey: TargetingPresetKey;
  validateFn: (value: any, parameterLabel: string) => string[];
}

export interface ITargetingPresetParam {
  key: TargetingPresetParamKey;
  label: string; // displayed title
  categoryKey: TargetingPresetCategory; // section category
  selectCategoryKey: TargetingPresetSelectCategory; // dropdown category
  isSuperUserOnly?: boolean; // make sure to also populate checkIfHidden
  checkIfHidden?: (publisherID: string, isSuperUser: boolean) => boolean; // check if publisher specific
  Component: (props: any) => JSX.Element;
  props?: Record<string, any>;
  apiKeys: TargetingPresetKey[]; // DO NOT INCLUDE KEYS THAT ARE USED IN CUSTOMUICONVERSIONS! IMPORTANT!!!
  customUIConversions?: ICustomPresetConversion;
  customValidation?: ICustomValidation;
  SummaryComponent: (props: any) => JSX.Element;
  summaryProps?: Record<string, any>;
}

export const TargetingPresetParameters: Record<TargetingPresetParamKey, ITargetingPresetParam> = {
  // SUPPLY CATEGORY
  // DEVICE
  TargetedDevices: {
    key: TargetingPresetParamKeys.TARGETED_DEVICES,
    label: 'Targeted Devices',
    categoryKey: TargetingPresetCategoryKeys.SUPPLY,
    selectCategoryKey: 'Devices',
    apiKeys: [
      TargetingPresetKeys.TARGETING_DEVICES,
      TargetingPresetKeys.TARGETING_DEVICES_IS_ALLOWED,
    ],
    Component: SelectWithIncludeAndLogic,
    props: {
      placeholder: 'All Devices Selected',
      singularLabel: 'Device',
      listLabel: 'Devices',
      listKey: 'Devices',
      valueKey: TargetingPresetKeys.TARGETING_DEVICES,
      allowKey: TargetingPresetKeys.TARGETING_DEVICES_IS_ALLOWED,
      convertResponseValue: convertDevicesResponseValue,
      convertSelectedValue: convertDevicesSelectedValue,
      key: TargetingPresetKeys.TARGETING_DEVICES,
    } as ISelectWithIncludeAndLogicProps,
    SummaryComponent: SelectedOptionsWithLogic,
    summaryProps: {
      listKey: 'Devices',
      valueKey: TargetingPresetKeys.TARGETING_DEVICES,
      allowKey: TargetingPresetKeys.TARGETING_DEVICES_IS_ALLOWED,
      convertValue: convertNumericIDsForSummaryZeroAll,
    },
  },
  TargetedDeviceTypes: {
    key: TargetingPresetParamKeys.TARGETED_DEVICE_TYPES,
    label: 'Targeted Device Types',
    categoryKey: TargetingPresetCategoryKeys.SUPPLY,
    selectCategoryKey: 'Devices',
    apiKeys: [
      TargetingPresetKeys.TARGETING_DEVICE_TYPES,
      TargetingPresetKeys.TARGETING_DEVICE_TYPES_IS_ALLOWED,
    ],
    Component: SelectWithIncludeAndLogic,
    props: {
      placeholder: 'All Device Types Selected',
      singularLabel: 'Device Type',
      listLabel: 'Device Types',
      listKey: 'DeviceTypes',
      valueKey: TargetingPresetKeys.TARGETING_DEVICE_TYPES,
      allowKey: TargetingPresetKeys.TARGETING_DEVICE_TYPES_IS_ALLOWED,
      convertResponseValue: convertDeviceTypesResponseValue,
      convertSelectedValue: convertDeviceTypesSelectedValue,
      key: TargetingPresetKeys.TARGETING_DEVICE_TYPES,
      disableSort: true,
      isSelectionRequired: true,
    } as ISelectWithIncludeAndLogicProps,
    SummaryComponent: SelectedOptionsWithLogic,
    summaryProps: {
      listKey: 'DeviceTypes',
      valueKey: TargetingPresetKeys.TARGETING_DEVICE_TYPES,
      allowKey: TargetingPresetKeys.TARGETING_DEVICE_TYPES_IS_ALLOWED,
      convertValue: convertNumericIDsForSummary,
    },
  },
  TargetedDeviceSubtypes: {
    key: TargetingPresetParamKeys.TARGETED_DEVICE_SUBTYPES,
    label: 'Targeted Device Subtypes',
    categoryKey: TargetingPresetCategoryKeys.SUPPLY,
    selectCategoryKey: 'Devices',
    apiKeys: [
      TargetingPresetKeys.TARGETING_DEVICE_SUB_TYPES,
      TargetingPresetKeys.TARGETING_DEVICE_SUB_TYPES_IS_ALLOWED,
    ],
    Component: SelectWithIncludeAndLogic,
    props: {
      placeholder: 'All Device SubTypes Selected',
      singularLabel: 'Device SubType',
      listLabel: 'Device SubTypes',
      listKey: 'DeviceSubTypes',
      valueKey: TargetingPresetKeys.TARGETING_DEVICE_SUB_TYPES,
      allowKey: TargetingPresetKeys.TARGETING_DEVICE_SUB_TYPES_IS_ALLOWED,
      convertResponseValue: convertDeviceSubTypesResponseValue,
      convertSelectedValue: convertDeviceSubTypesSelectedValue,
      key: TargetingPresetKeys.TARGETING_DEVICE_TYPES,
      disableSort: true,
    } as ISelectWithIncludeAndLogicProps,
    // TODO: CHANGE LATER WHEN OFFICIAL RELEASE
    // checkIfHidden: (publisherID) => !isPublisherSamsung(publisherID),
    checkIfHidden: (publisherID) => !['1', '518', '394'].includes(publisherID),
    SummaryComponent: SelectedOptionsWithLogic,
    summaryProps: {
      listKey: 'DeviceSubTypes',
      valueKey: TargetingPresetKeys.TARGETING_DEVICE_SUB_TYPES,
      allowKey: TargetingPresetKeys.TARGETING_DEVICE_SUB_TYPES_IS_ALLOWED,
      convertValue: convertNumericIDsForSummaryZeroAll,
    },
  },
  IPFilter: {
    key: TargetingPresetParamKeys.IP_FILTER,
    label: 'IP Filters *',
    categoryKey: TargetingPresetCategoryKeys.SUPPLY,
    selectCategoryKey: 'Devices',
    apiKeys: [TargetingPresetKeys.IP_FILTER],
    isSuperUserOnly: true,
    checkIfHidden: (_, isSuperUser) => !isSuperUser,
    customValidation: {
      validateKey: TargetingPresetKeys.IP_FILTER,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.IP_FILTER,
      placeholder: generateGenericPlaceholder('IP Filters'),
      valueKey: TargetingPresetKeys.IP_FILTER,
      tooltip: `Match this value to the IP set in your request to Publica's endpoint. Can be comma separated.`,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.IP_FILTER,
    } as IIncludeExcludeStringProps,
  },
  // CONTENT
  ContentChannel: {
    key: TargetingPresetParamKeys.CONTENT_CHANNEL,
    label: 'Content Channel',
    categoryKey: TargetingPresetCategoryKeys.SUPPLY,
    selectCategoryKey: 'Content',
    apiKeys: [
      TargetingPresetKeys.TARGETING_CONTENT_CHANNEL,
      TargetingPresetKeys.TARGETING_CONTENT_CHANNEL_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: TargetingPresetKeys.TARGETING_CONTENT_CHANNEL,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CONTENT_CHANNEL,
      placeholder: generateContentPlaceholder('Content Channel'),
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_CHANNEL,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_CHANNEL_IS_ALLOWED,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_CHANNEL,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_CHANNEL_IS_ALLOWED,
    } as IIncludeExcludeStringProps,
  },
  ContentDistributor: {
    key: TargetingPresetParamKeys.CONTENT_DISTRIBUTOR,
    label: 'Content Distributor',
    categoryKey: TargetingPresetCategoryKeys.SUPPLY,
    selectCategoryKey: 'Content',
    apiKeys: [
      TargetingPresetKeys.TARGETING_CONTENT_DISTRIBUTOR,
      TargetingPresetKeys.TARGETING_CONTENT_DISTRIBUTOR_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: TargetingPresetKeys.TARGETING_CONTENT_DISTRIBUTOR,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CONTENT_DISTRIBUTOR,
      placeholder: generateContentPlaceholder('Content Distributor'),
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_DISTRIBUTOR,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_DISTRIBUTOR_IS_ALLOWED,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_DISTRIBUTOR,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_DISTRIBUTOR_IS_ALLOWED,
    } as IIncludeExcludeStringProps,
  },
  ContentGenre: {
    key: TargetingPresetParamKeys.CONTENT_GENRE,
    label: 'Content Genre',
    categoryKey: TargetingPresetCategoryKeys.SUPPLY,
    selectCategoryKey: 'Content',
    apiKeys: [
      TargetingPresetKeys.TARGETING_CONTENT_GENRE,
      TargetingPresetKeys.TARGETING_CONTENT_GENRE_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: TargetingPresetKeys.TARGETING_CONTENT_GENRE,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CONTENT_GENRE,
      placeholder: generateContentPlaceholder('Content Genre'),
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_GENRE,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_GENRE_IS_ALLOWED,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_GENRE,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_GENRE_IS_ALLOWED,
    } as IIncludeExcludeStringProps,
  },
  ContentID: {
    key: TargetingPresetParamKeys.CONTENT_ID,
    label: 'Content ID',
    categoryKey: TargetingPresetCategoryKeys.SUPPLY,
    selectCategoryKey: 'Content',
    apiKeys: [
      TargetingPresetKeys.TARGETING_CONTENT_ID,
      TargetingPresetKeys.TARGETING_CONTENT_ID_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: TargetingPresetKeys.TARGETING_CONTENT_ID,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CONTENT_ID,
      placeholder: generateContentPlaceholder('Content ID'),
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_ID,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_ID_IS_ALLOWED,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_ID,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_ID_IS_ALLOWED,
    } as IIncludeExcludeStringProps,
  },
  ContentNetwork: {
    key: TargetingPresetParamKeys.CONTENT_NETWORK,
    label: 'Content Network',
    categoryKey: TargetingPresetCategoryKeys.SUPPLY,
    selectCategoryKey: 'Content',
    apiKeys: [
      TargetingPresetKeys.TARGETING_CONTENT_NETWORK,
      TargetingPresetKeys.TARGETING_CONTENT_NETWORK_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: TargetingPresetKeys.TARGETING_CONTENT_NETWORK,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CONTENT_NETWORK,
      placeholder: generateContentPlaceholder('Content Network'),
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_NETWORK,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_NETWORK_IS_ALLOWED,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_NETWORK,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_NETWORK_IS_ALLOWED,
    } as IIncludeExcludeStringProps,
  },
  ContentProducer: {
    key: TargetingPresetParamKeys.CONTENT_PRODUCER,
    label: 'Content Producer',
    categoryKey: TargetingPresetCategoryKeys.SUPPLY,
    selectCategoryKey: 'Content',
    apiKeys: [
      TargetingPresetKeys.TARGETING_CONTENT_PRODUCER,
      TargetingPresetKeys.TARGETING_CONTENT_PRODUCER_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: TargetingPresetKeys.TARGETING_CONTENT_PRODUCER,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CONTENT_PRODUCER,
      placeholder: generateContentPlaceholder('Content Producer'),
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_PRODUCER,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_PRODUCER_IS_ALLOWED,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_PRODUCER,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_PRODUCER_IS_ALLOWED,
    } as IIncludeExcludeStringProps,
  },
  ContentRating: {
    key: TargetingPresetParamKeys.CONTENT_RATING,
    label: 'Content Rating',
    categoryKey: TargetingPresetCategoryKeys.SUPPLY,
    selectCategoryKey: 'Content',
    apiKeys: [
      TargetingPresetKeys.TARGETING_CONTENT_RATING,
      TargetingPresetKeys.TARGETING_CONTENT_RATING_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: TargetingPresetKeys.TARGETING_CONTENT_RATING,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CONTENT_RATING,
      placeholder: generateContentPlaceholder('Content Rating'),
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_RATING,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_RATING_IS_ALLOWED,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_RATING,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_RATING_IS_ALLOWED,
    } as IIncludeExcludeStringProps,
  },
  ContentSeries: {
    key: TargetingPresetParamKeys.CONTENT_SERIES,
    label: 'Content Series',
    categoryKey: TargetingPresetCategoryKeys.SUPPLY,
    selectCategoryKey: 'Content',
    apiKeys: [
      TargetingPresetKeys.TARGETING_CONTENT_SERIES,
      TargetingPresetKeys.TARGETING_CONTENT_SERIES_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: TargetingPresetKeys.TARGETING_CONTENT_SERIES,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CONTENT_SERIES,
      placeholder: generateContentPlaceholder('Content Series'),
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_SERIES,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_SERIES_IS_ALLOWED,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_SERIES,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_SERIES_IS_ALLOWED,
    } as IIncludeExcludeStringProps,
  },
  ContentTitle: {
    key: TargetingPresetParamKeys.CONTENT_TITLE,
    label: 'Content Title',
    categoryKey: TargetingPresetCategoryKeys.SUPPLY,
    selectCategoryKey: 'Content',
    apiKeys: [
      TargetingPresetKeys.TARGETING_CONTENT_TITLE,
      TargetingPresetKeys.TARGETING_CONTENT_TITLE_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: TargetingPresetKeys.TARGETING_CONTENT_TITLE,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CONTENT_TITLE,
      placeholder: generateContentPlaceholder('Content Title'),
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_TITLE,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_TITLE_IS_ALLOWED,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_TITLE,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_TITLE_IS_ALLOWED,
    } as IIncludeExcludeStringProps,
  },
  ContentCategory: {
    key: TargetingPresetParamKeys.CONTENT_CATEGORY,
    label: 'Content Category',
    categoryKey: TargetingPresetCategoryKeys.SUPPLY,
    selectCategoryKey: 'Content',
    apiKeys: [
      TargetingPresetKeys.TARGETING_CONTENT_CATEGORY,
      TargetingPresetKeys.TARGETING_CONTENT_CATEGORY_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: TargetingPresetKeys.TARGETING_CONTENT_CATEGORY,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CONTENT_CATEGORY,
      placeholder: generateContentPlaceholder('Content Category'),
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_CATEGORY,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_CATEGORY_IS_ALLOWED,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_CATEGORY,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_CATEGORY_IS_ALLOWED,
    } as IIncludeExcludeStringProps,
  },
  // STREAMS
  TargetedLivestreams: {
    key: TargetingPresetParamKeys.TARGETED_LIVESTREAMS,
    label: 'Targeted Livestreams',
    categoryKey: TargetingPresetCategoryKeys.SUPPLY,
    selectCategoryKey: 'Streams',
    apiKeys: [TargetingPresetKeys.TARGETING_LIVESTREAM],
    Component: SelectWithIncludeAndLogic,
    props: {
      placeholder: 'All Livestreams Selected',
      singularLabel: 'Livestream',
      listLabel: 'Livestreams',
      listKey: 'Livestreams',
      valueKey: TargetingPresetKeys.TARGETING_LIVESTREAM,
      convertResponseValue: convertLivestreamResponseValue,
      convertSelectedValue: convertLivestreamSelectedValue,
      key: TargetingPresetKeys.TARGETING_LIVESTREAM,
      disableSort: true,
    } as ISelectWithIncludeAndLogicProps,
    SummaryComponent: SelectedOptionsWithLogic,
    summaryProps: {
      listKey: 'Livestreams',
      valueKey: TargetingPresetKeys.TARGETING_LIVESTREAM,
      convertValue: convertNumericIDsForSummary,
    },
  },
  // APP
  AppBundleIDs: {
    key: TargetingPresetParamKeys.APP_BUNDLE_IDS,
    label: 'App Bundle IDs',
    categoryKey: TargetingPresetCategoryKeys.SUPPLY,
    selectCategoryKey: 'App',
    apiKeys: [
      TargetingPresetKeys.TARGETING_APP_BUNDLE_ID,
      TargetingPresetKeys.TARGETING_APP_BUNDLE_ID_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: TargetingPresetKeys.TARGETING_APP_BUNDLE_ID,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.APP_BUNDLE_IDS,
      placeholder: generateGenericPlaceholder('App Bundle IDs'),
      valueKey: TargetingPresetKeys.TARGETING_APP_BUNDLE_ID,
      allowKey: TargetingPresetKeys.TARGETING_APP_BUNDLE_ID_IS_ALLOWED,
      isAllowTypeBool: true,
      tooltip: AppBundleTooltipMessage,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.TARGETING_APP_BUNDLE_ID,
      allowKey: TargetingPresetKeys.TARGETING_APP_BUNDLE_ID_IS_ALLOWED,
      isAllowTypeBool: true,
    } as IIncludeExcludeStringProps,
  },
  // SITE PAGES
  SitePages: {
    key: TargetingPresetParamKeys.SITE_PAGES,
    label: 'Site Pages',
    categoryKey: TargetingPresetCategoryKeys.SUPPLY,
    selectCategoryKey: 'Site Pages',
    apiKeys: [
      TargetingPresetKeys.TARGETING_SITE_PAGES,
      TargetingPresetKeys.TARGETING_SITE_PAGES_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: TargetingPresetKeys.TARGETING_SITE_PAGES,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.SITE_PAGES,
      placeholder: generateGenericPlaceholder('Site Pages'),
      valueKey: TargetingPresetKeys.TARGETING_SITE_PAGES,
      allowKey: TargetingPresetKeys.TARGETING_SITE_PAGES_IS_ALLOWED,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.TARGETING_SITE_PAGES,
      allowKey: TargetingPresetKeys.TARGETING_SITE_PAGES_IS_ALLOWED,
    } as IIncludeExcludeStringProps,
  },
  // SITE DOMAINS
  SiteDomains: {
    key: TargetingPresetParamKeys.SITE_DOMAINS,
    label: 'Site Domains',
    categoryKey: TargetingPresetCategoryKeys.SUPPLY,
    selectCategoryKey: 'Site Domains',
    apiKeys: [
      TargetingPresetKeys.TARGETING_SITE_DOMAIN,
      TargetingPresetKeys.TARGETING_SITE_DOMAIN_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: TargetingPresetKeys.TARGETING_SITE_DOMAIN,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.SITE_DOMAINS,
      placeholder: generateGenericPlaceholder('Site Domains'),
      valueKey: TargetingPresetKeys.TARGETING_SITE_DOMAIN,
      allowKey: TargetingPresetKeys.TARGETING_SITE_DOMAIN_IS_ALLOWED,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.TARGETING_SITE_DOMAIN,
      allowKey: TargetingPresetKeys.TARGETING_SITE_DOMAIN_IS_ALLOWED,
    } as IIncludeExcludeStringProps,
  },
  // USERS CATEGORY
  // GEO
  TargetedRegions: {
    key: TargetingPresetParamKeys.TARGETED_REGIONS,
    label: 'Targeted Regions',
    categoryKey: TargetingPresetCategoryKeys.USERS,
    selectCategoryKey: 'Geo',
    apiKeys: [
      TargetingPresetKeys.TARGETING_COUNTRY_GROUP_IDS,
      TargetingPresetKeys.TARGETING_COUNTRY_GROUP_IDS_IS_ALLOWED,
    ],
    Component: RegionsEditor,
    // TODO: FIXME
    SummaryComponent: SelectedOptionsWithLogic,
    summaryProps: {
      listKey: 'CountryGroups',
      valueKey: 'CountryGroups',
      allowKey: TargetingPresetKeys.TARGETING_COUNTRY_GROUP_IDS_IS_ALLOWED,
      convertValue: convertIDNameForSummary,
    },
  },
  TargetedCountries: {
    key: TargetingPresetParamKeys.TARGETED_COUNTRIES,
    label: 'Targeted Countries',
    categoryKey: TargetingPresetCategoryKeys.USERS,
    selectCategoryKey: 'Geo',
    apiKeys: [
      TargetingPresetKeys.TARGETING_DEVICE_COUNTRY,
      TargetingPresetKeys.TARGETING_DEVICE_COUNTRY_IS_ALLOWED,
    ],
    Component: SelectWithIncludeAndLogic,
    props: {
      placeholder: 'Select Countries',
      singularLabel: 'Country',
      extractStringToSort: removeEmojiPrefixFromLabel,
      listLabel: 'Countries',
      listKey: 'Countries',
      valueKey: TargetingPresetKeys.TARGETING_DEVICE_COUNTRY,
      allowKey: TargetingPresetKeys.TARGETING_DEVICE_COUNTRY_IS_ALLOWED,
      convertResponseValue: convertStringIntoArray,
      convertSelectedValue: convertSetIntoString,
      key: TargetingPresetKeys.TARGETING_DEVICE_COUNTRY,
    } as ISelectWithIncludeAndLogicProps,
    SummaryComponent: SelectedOptionsWithLogic,
    summaryProps: {
      valueKey: TargetingPresetKeys.TARGETING_DEVICE_COUNTRY,
      allowKey: TargetingPresetKeys.TARGETING_DEVICE_COUNTRY_IS_ALLOWED,
      listKey: 'Countries',
      convertValue: convertCountryIDsForSummary,
    },
  },
  // THIS IS A SPECIAL CASE AND REQUIRES CUSTOM COMPONENTS
  LocalTargetingAllowed: {
    key: TargetingPresetParamKeys.LOCAL_TARGETING_ALLOWED,
    label: 'Local Targeting Included',
    categoryKey: TargetingPresetCategoryKeys.USERS,
    selectCategoryKey: 'Geo',
    apiKeys: [TargetingPresetKeys.LOCAL_TARGETING_ALLOWED_LOGIC],
    Component: LocalTargetingBuilder,
    props: {
      apiKey: TargetingPresetKeys.LOCAL_TARGETING_ALLOWED,
      logicKey: TargetingPresetKeys.LOCAL_TARGETING_ALLOWED_LOGIC,
      uiKey: TargetingPresetKeys.LOCAL_TARGETING_ALLOWED_UI,
    } as ILocalTargetingBuilderProps,
    customUIConversions: {
      apiKey: TargetingPresetKeys.LOCAL_TARGETING_ALLOWED,
      uiKey: TargetingPresetKeys.LOCAL_TARGETING_ALLOWED_UI,
      convertResponse: parseLocalTargetingForUI,
      convertPayload: convertLocalTargetingUI,
    },
    SummaryComponent: LocalTargetingSummary,
    summaryProps: {
      valueKey: TargetingPresetParamKeys.LOCAL_TARGETING_ALLOWED,
      logicKey: TargetingPresetKeys.LOCAL_TARGETING_ALLOWED_LOGIC,
    },
  },
  // THIS IS A SPECIAL CASE AND REQUIRES CUSTOM COMPONENTS
  LocalTargetingDisallowed: {
    key: TargetingPresetParamKeys.LOCAL_TARGETING_DISALLOWED,
    label: 'Local Targeting Excluded',
    categoryKey: TargetingPresetCategoryKeys.USERS,
    selectCategoryKey: 'Geo',
    apiKeys: [TargetingPresetKeys.LOCAL_TARGETING_DISALLOWED_LOGIC],
    Component: LocalTargetingBuilder,
    props: {
      apiKey: TargetingPresetKeys.LOCAL_TARGETING_DISALLOWED,
      logicKey: TargetingPresetKeys.LOCAL_TARGETING_DISALLOWED_LOGIC,
      uiKey: TargetingPresetKeys.LOCAL_TARGETING_DISALLOWED_UI,
    } as ILocalTargetingBuilderProps,
    customUIConversions: {
      apiKey: TargetingPresetKeys.LOCAL_TARGETING_DISALLOWED,
      uiKey: TargetingPresetKeys.LOCAL_TARGETING_DISALLOWED_UI,
      convertResponse: parseLocalTargetingForUI,
      convertPayload: convertLocalTargetingUI,
    },
    SummaryComponent: LocalTargetingSummary,
    summaryProps: {
      valueKey: TargetingPresetParamKeys.LOCAL_TARGETING_DISALLOWED,
      logicKey: TargetingPresetKeys.LOCAL_TARGETING_DISALLOWED_LOGIC,
    },
  },
  // THIS IS A SPECIAL CASE AND REQUIRES CUSTOM COMPONENTS
  Dayparting: {
    key: TargetingPresetParamKeys.DAYPARTING,
    label: 'Dayparting',
    categoryKey: TargetingPresetCategoryKeys.USERS,
    selectCategoryKey: 'Time',
    apiKeys: [TargetingPresetKeys.DAYPARTING_IS_ALLOWED],
    Component: DaypartingBuilder,
    customUIConversions: {
      apiKey: TargetingPresetKeys.DAYPARTING,
      uiKey: TargetingPresetKeys.DAYPARTING_UI,
      convertResponse: parseDaypartingForUI,
      convertPayload: convertDaypartingUI,
    },
    SummaryComponent: DaypartingSummary,
    summaryProps: {
      valueKey: TargetingPresetKeys.DAYPARTING,
      allowKey: TargetingPresetKeys.DAYPARTING_IS_ALLOWED,
    },
  },
  // SEGMENTS
  AudienceSegments: {
    key: TargetingPresetParamKeys.AUDIENCE_SEGMENTS,
    label: 'Audience Segments',
    categoryKey: TargetingPresetCategoryKeys.USERS,
    selectCategoryKey: 'Segments',
    apiKeys: [
      TargetingPresetKeys.TARGETING_AUDIENCE_SEGMENT_IDS,
      TargetingPresetKeys.TARGETING_AUDIENCE_SEGMENT_IDS_IS_ALLOWED,
      TargetingPresetKeys.SEGMENT_TARGETING,
    ],
    Component: SelectWithIncludeAndLogic,
    props: {
      placeholder: 'Select Audience Segments',
      singularLabel: 'Audience Segment',
      listLabel: 'Audience Segments',
      listKey: 'AudienceSegments',
      valueKey: TargetingPresetKeys.TARGETING_AUDIENCE_SEGMENT_IDS,
      allowKey: TargetingPresetKeys.TARGETING_AUDIENCE_SEGMENT_IDS_IS_ALLOWED,
      logicKey: TargetingPresetKeys.SEGMENT_TARGETING,
      enableActiveFilter: true,
      enableLabelTooltip: true,
      displayID: true,
      key: TargetingPresetKeys.TARGETING_AUDIENCE_SEGMENT_IDS,
      tooltip: 'Choose which audience segment to bid on.',
    } as ISelectWithIncludeAndLogicProps,
    SummaryComponent: SelectedOptionsWithLogic,
    summaryProps: {
      listKey: 'AudienceSegments',
      valueKey: 'AudienceSegments',
      allowKey: TargetingPresetKeys.TARGETING_AUDIENCE_SEGMENT_IDS_IS_ALLOWED,
      logicKey: TargetingPresetKeys.SEGMENT_TARGETING,
      convertValue: convertIDNameForSummary,
    },
  },
  ContextualSegments: {
    key: TargetingPresetParamKeys.CONTEXTUAL_SEGMENTS,
    label: 'Contextual Segments',
    categoryKey: TargetingPresetCategoryKeys.USERS,
    selectCategoryKey: 'Segments',
    apiKeys: [TargetingPresetKeys.TARGETING_CONTEXTUAL_SEGMENT_IDS],
    Component: SelectWithIncludeAndLogic,
    props: {
      placeholder: 'Select Contextual Segment',
      singularLabel: 'Contextual Segment',
      listLabel: 'Contextual Segments',
      listKey: 'ContextualSegments',
      selectedLimit: 1,
      valueKey: TargetingPresetKeys.TARGETING_CONTEXTUAL_SEGMENT_IDS,
      enableActiveFilter: true,
      enableLabelTooltip: true,
      displayID: true,
      key: TargetingPresetKeys.TARGETING_CONTEXTUAL_SEGMENT_IDS,
      tooltip: 'Choose which contextual segment to bid on.',
    } as ISelectWithIncludeAndLogicProps,
    SummaryComponent: SelectedOptionsWithLogic,
    summaryProps: {
      listKey: 'ContextualSegments',
      valueKey: 'ContextualSegments',
      convertValue: convertIDNameForSummary,
    },
  },
  // PRIVACY
  COPPA: {
    key: TargetingPresetParamKeys.COPPA,
    label: 'COPPA',
    categoryKey: TargetingPresetCategoryKeys.USERS,
    selectCategoryKey: 'Privacy',
    apiKeys: [TargetingPresetKeys.TARGETING_COPPA],
    Component: SelectWithIncludeAndLogic,
    props: {
      placeholder: 'Select COPPA Value',
      singularLabel: 'COPPA Value',
      listLabel: 'COPPA',
      listKey: 'COPPA',
      valueKey: TargetingPresetKeys.TARGETING_COPPA,
      selectedLimit: 1,
      isSelectionRequired: true,
      convertResponseValue: convertSingleNumberIntoArray,
      convertSelectedValue: convertSetIntoSingleNumber,
      key: TargetingPresetKeys.TARGETING_COPPA,
      disableSort: true,
    } as ISelectWithIncludeAndLogicProps,
    SummaryComponent: SelectedOptionsWithLogic,
    summaryProps: {
      listKey: 'COPPA',
      valueKey: TargetingPresetKeys.TARGETING_COPPA,
      convertValue: convertSingleNumericIDForSummaryZeroDefault,
    },
  },
  LimitedAdTracking: {
    key: TargetingPresetParamKeys.LIMITED_AD_TRACKING,
    label: 'Limited Ad Tracking',
    categoryKey: TargetingPresetCategoryKeys.USERS,
    selectCategoryKey: 'Privacy',
    apiKeys: [TargetingPresetKeys.TARGETING_LIMITED_AD_TRACKING],
    Component: SelectWithIncludeAndLogic,
    props: {
      placeholder: 'Select Limited Ad Tracking',
      singularLabel: 'Limited Ad Tracking',
      listLabel: 'Limited Ad Tracking',
      listKey: 'LimitedAdTracking',
      valueKey: TargetingPresetKeys.TARGETING_LIMITED_AD_TRACKING,
      selectedLimit: 1,
      isSelectionRequired: true,
      convertResponseValue: convertSingleNumberIntoArray,
      convertSelectedValue: convertSetIntoSingleNumber,
      key: TargetingPresetKeys.TARGETING_LIMITED_AD_TRACKING,
      disableSort: true,
    } as ISelectWithIncludeAndLogicProps,
    SummaryComponent: SelectedOptionsWithLogic,
    summaryProps: {
      listKey: 'LimitedAdTracking',
      valueKey: TargetingPresetKeys.TARGETING_LIMITED_AD_TRACKING,
      convertValue: convertSingleNumericIDForSummaryZeroDefault,
    },
  },
  CCPA: {
    key: TargetingPresetParamKeys.CCPA,
    label: 'CCPA',
    categoryKey: TargetingPresetCategoryKeys.USERS,
    selectCategoryKey: 'Privacy',
    apiKeys: [TargetingPresetKeys.TARGETING_CCPA],
    Component: SelectWithIncludeAndLogic,
    props: {
      placeholder: 'Select CCPA Value',
      singularLabel: 'CCPA Value',
      listLabel: 'CCPA',
      listKey: 'CCPA',
      valueKey: TargetingPresetKeys.TARGETING_CCPA,
      selectedLimit: 1,
      isSelectionRequired: true,
      convertResponseValue: convertSingleNumberIntoArray,
      convertSelectedValue: convertSetIntoSingleNumber,
      key: TargetingPresetKeys.TARGETING_CCPA,
      disableSort: true,
    } as ISelectWithIncludeAndLogicProps,
    SummaryComponent: SelectedOptionsWithLogic,
    summaryProps: {
      listKey: 'CCPA',
      valueKey: TargetingPresetKeys.TARGETING_CCPA,
      convertValue: convertSingleNumericIDForSummaryZeroDefault,
    },
  },
  // THIS IS A SPECIAL CASE AND REQUIRES CUSTOM COMPONENTS
  GDPR: {
    key: TargetingPresetParamKeys.GDPR,
    label: 'GDPR',
    categoryKey: TargetingPresetCategoryKeys.USERS,
    selectCategoryKey: 'Privacy',
    apiKeys: [TargetingPresetKeys.TARGETING_GDPR, TargetingPresetKeys.TARGETING_GDPR_CONSENT],
    Component: GDPRSelect,
    props: {},
    SummaryComponent: GDPRSummary,
    summaryProps: {},
  },
  // CUSTOMS CATEGORY
  // CUSTOM
  Custom4: {
    key: TargetingPresetParamKeys.CUSTOM_4,
    label: 'Custom 4',
    categoryKey: TargetingPresetCategoryKeys.CUSTOMS,
    selectCategoryKey: 'Custom',
    apiKeys: [TargetingPresetKeys.CUSTOM_4, TargetingPresetKeys.CUSTOM_4_INCLUSION],
    customValidation: {
      validateKey: TargetingPresetKeys.CUSTOM_4,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CUSTOM_4,
      placeholder: generateCustomPlaceholder('Custom 4'),
      valueKey: TargetingPresetKeys.CUSTOM_4,
      allowKey: TargetingPresetKeys.CUSTOM_4_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.CUSTOM_4,
      allowKey: TargetingPresetKeys.CUSTOM_4_INCLUSION,
    } as IIncludeExcludeStringProps,
  },
  Custom5: {
    key: TargetingPresetParamKeys.CUSTOM_5,
    label: 'Custom 5',
    categoryKey: TargetingPresetCategoryKeys.CUSTOMS,
    selectCategoryKey: 'Custom',
    apiKeys: [TargetingPresetKeys.CUSTOM_5, TargetingPresetKeys.CUSTOM_5_INCLUSION],
    customValidation: {
      validateKey: TargetingPresetKeys.CUSTOM_5,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CUSTOM_5,
      placeholder: generateCustomPlaceholder('Custom 5'),
      valueKey: TargetingPresetKeys.CUSTOM_5,
      allowKey: TargetingPresetKeys.CUSTOM_5_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.CUSTOM_5,
      allowKey: TargetingPresetKeys.CUSTOM_5_INCLUSION,
    } as IIncludeExcludeStringProps,
  },
  Custom6: {
    key: TargetingPresetParamKeys.CUSTOM_6,
    label: 'Custom 6',
    categoryKey: TargetingPresetCategoryKeys.CUSTOMS,
    selectCategoryKey: 'Custom',
    apiKeys: [TargetingPresetKeys.CUSTOM_6, TargetingPresetKeys.CUSTOM_6_INCLUSION],
    customValidation: {
      validateKey: TargetingPresetKeys.CUSTOM_6,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CUSTOM_6,
      placeholder: generateCustomPlaceholder('Custom 6'),
      valueKey: TargetingPresetKeys.CUSTOM_6,
      allowKey: TargetingPresetKeys.CUSTOM_6_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.CUSTOM_6,
      allowKey: TargetingPresetKeys.CUSTOM_6_INCLUSION,
    } as IIncludeExcludeStringProps,
  },
  Custom7: {
    key: TargetingPresetParamKeys.CUSTOM_7,
    label: 'Custom 7',
    categoryKey: TargetingPresetCategoryKeys.CUSTOMS,
    selectCategoryKey: 'Custom',
    apiKeys: [TargetingPresetKeys.CUSTOM_7, TargetingPresetKeys.CUSTOM_7_INCLUSION],
    customValidation: {
      validateKey: TargetingPresetKeys.CUSTOM_7,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CUSTOM_7,
      placeholder: generateCustomPlaceholder('Custom 7'),
      valueKey: TargetingPresetKeys.CUSTOM_7,
      allowKey: TargetingPresetKeys.CUSTOM_7_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.CUSTOM_7,
      allowKey: TargetingPresetKeys.CUSTOM_7_INCLUSION,
    } as IIncludeExcludeStringProps,
  },
  Custom8: {
    key: TargetingPresetParamKeys.CUSTOM_8,
    label: 'Custom 8',
    categoryKey: TargetingPresetCategoryKeys.CUSTOMS,
    selectCategoryKey: 'Custom',
    apiKeys: [TargetingPresetKeys.CUSTOM_8, TargetingPresetKeys.CUSTOM_8_INCLUSION],
    customValidation: {
      validateKey: TargetingPresetKeys.CUSTOM_8,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CUSTOM_8,
      placeholder: generateCustomPlaceholder('Custom 8'),
      valueKey: TargetingPresetKeys.CUSTOM_8,
      allowKey: TargetingPresetKeys.CUSTOM_8_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.CUSTOM_8,
      allowKey: TargetingPresetKeys.CUSTOM_8_INCLUSION,
    } as IIncludeExcludeStringProps,
  },
  Custom9: {
    key: TargetingPresetParamKeys.CUSTOM_9,
    label: 'Custom 9',
    categoryKey: TargetingPresetCategoryKeys.CUSTOMS,
    selectCategoryKey: 'Custom',
    apiKeys: [TargetingPresetKeys.CUSTOM_9, TargetingPresetKeys.CUSTOM_9_INCLUSION],
    customValidation: {
      validateKey: TargetingPresetKeys.CUSTOM_9,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CUSTOM_9,
      placeholder: generateCustomPlaceholder('Custom 9'),
      valueKey: TargetingPresetKeys.CUSTOM_9,
      allowKey: TargetingPresetKeys.CUSTOM_9_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.CUSTOM_9,
      allowKey: TargetingPresetKeys.CUSTOM_9_INCLUSION,
    } as IIncludeExcludeStringProps,
  },
  Custom10: {
    key: TargetingPresetParamKeys.CUSTOM_10,
    label: 'Custom 10',
    categoryKey: TargetingPresetCategoryKeys.CUSTOMS,
    selectCategoryKey: 'Custom',
    apiKeys: [TargetingPresetKeys.CUSTOM_10, TargetingPresetKeys.CUSTOM_10_INCLUSION],
    customValidation: {
      validateKey: TargetingPresetKeys.CUSTOM_10,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CUSTOM_10,
      placeholder: generateCustomPlaceholder('Custom 10'),
      valueKey: TargetingPresetKeys.CUSTOM_10,
      allowKey: TargetingPresetKeys.CUSTOM_10_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.CUSTOM_10,
      allowKey: TargetingPresetKeys.CUSTOM_10_INCLUSION,
    } as IIncludeExcludeStringProps,
  },
  Custom11: {
    key: TargetingPresetParamKeys.CUSTOM_11,
    label: 'Custom 11',
    categoryKey: TargetingPresetCategoryKeys.CUSTOMS,
    selectCategoryKey: 'Custom',
    apiKeys: [TargetingPresetKeys.CUSTOM_11, TargetingPresetKeys.CUSTOM_11_INCLUSION],
    customValidation: {
      validateKey: TargetingPresetKeys.CUSTOM_11,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CUSTOM_11,
      placeholder: generateCustomPlaceholder('Custom 11'),
      valueKey: TargetingPresetKeys.CUSTOM_11,
      allowKey: TargetingPresetKeys.CUSTOM_11_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.CUSTOM_11,
      allowKey: TargetingPresetKeys.CUSTOM_11_INCLUSION,
    } as IIncludeExcludeStringProps,
  },
  Custom12: {
    key: TargetingPresetParamKeys.CUSTOM_12,
    label: 'Custom 12',
    categoryKey: TargetingPresetCategoryKeys.CUSTOMS,
    selectCategoryKey: 'Custom',
    apiKeys: [TargetingPresetKeys.CUSTOM_12, TargetingPresetKeys.CUSTOM_12_INCLUSION],
    customValidation: {
      validateKey: TargetingPresetKeys.CUSTOM_12,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CUSTOM_12,
      placeholder: generateCustomPlaceholder('Custom 12'),
      valueKey: TargetingPresetKeys.CUSTOM_12,
      allowKey: TargetingPresetKeys.CUSTOM_12_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.CUSTOM_12,
      allowKey: TargetingPresetKeys.CUSTOM_12_INCLUSION,
    } as IIncludeExcludeStringProps,
  },
  Custom13: {
    key: TargetingPresetParamKeys.CUSTOM_13,
    label: 'Custom 13',
    categoryKey: TargetingPresetCategoryKeys.CUSTOMS,
    selectCategoryKey: 'Custom',
    apiKeys: [TargetingPresetKeys.CUSTOM_13, TargetingPresetKeys.CUSTOM_13_INCLUSION],
    customValidation: {
      validateKey: TargetingPresetKeys.CUSTOM_13,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CUSTOM_13,
      placeholder: generateCustomPlaceholder('Custom 13'),
      valueKey: TargetingPresetKeys.CUSTOM_13,
      allowKey: TargetingPresetKeys.CUSTOM_13_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.CUSTOM_13,
      allowKey: TargetingPresetKeys.CUSTOM_13_INCLUSION,
    } as IIncludeExcludeStringProps,
  },
  Custom14: {
    key: TargetingPresetParamKeys.CUSTOM_14,
    label: 'Custom 14',
    categoryKey: TargetingPresetCategoryKeys.CUSTOMS,
    selectCategoryKey: 'Custom',
    apiKeys: [TargetingPresetKeys.CUSTOM_14, TargetingPresetKeys.CUSTOM_14_INCLUSION],
    customValidation: {
      validateKey: TargetingPresetKeys.CUSTOM_14,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CUSTOM_14,
      placeholder: generateCustomPlaceholder('Custom 14'),
      valueKey: TargetingPresetKeys.CUSTOM_14,
      allowKey: TargetingPresetKeys.CUSTOM_14_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.CUSTOM_14,
      allowKey: TargetingPresetKeys.CUSTOM_14_INCLUSION,
    } as IIncludeExcludeStringProps,
  },
  Custom15: {
    key: TargetingPresetParamKeys.CUSTOM_15,
    label: 'Custom 15',
    categoryKey: TargetingPresetCategoryKeys.CUSTOMS,
    selectCategoryKey: 'Custom',
    apiKeys: [TargetingPresetKeys.CUSTOM_15, TargetingPresetKeys.CUSTOM_15_INCLUSION],
    customValidation: {
      validateKey: TargetingPresetKeys.CUSTOM_15,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CUSTOM_15,
      placeholder: generateCustomPlaceholder('Custom 15'),
      valueKey: TargetingPresetKeys.CUSTOM_15,
      allowKey: TargetingPresetKeys.CUSTOM_15_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.CUSTOM_15,
      allowKey: TargetingPresetKeys.CUSTOM_15_INCLUSION,
    } as IIncludeExcludeStringProps,
  },
  Custom16: {
    key: TargetingPresetParamKeys.CUSTOM_16,
    label: 'Custom 16',
    categoryKey: TargetingPresetCategoryKeys.CUSTOMS,
    selectCategoryKey: 'Custom',
    apiKeys: [TargetingPresetKeys.CUSTOM_16, TargetingPresetKeys.CUSTOM_16_INCLUSION],
    customValidation: {
      validateKey: TargetingPresetKeys.CUSTOM_16,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CUSTOM_16,
      placeholder: generateCustomPlaceholder('Custom 16'),
      valueKey: TargetingPresetKeys.CUSTOM_16,
      allowKey: TargetingPresetKeys.CUSTOM_16_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.CUSTOM_16,
      allowKey: TargetingPresetKeys.CUSTOM_16_INCLUSION,
    } as IIncludeExcludeStringProps,
  },
  Custom17: {
    key: TargetingPresetParamKeys.CUSTOM_17,
    label: 'Custom 17',
    categoryKey: TargetingPresetCategoryKeys.CUSTOMS,
    selectCategoryKey: 'Custom',
    apiKeys: [TargetingPresetKeys.CUSTOM_17, TargetingPresetKeys.CUSTOM_17_INCLUSION],
    customValidation: {
      validateKey: TargetingPresetKeys.CUSTOM_17,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CUSTOM_17,
      placeholder: generateCustomPlaceholder('Custom 17'),
      valueKey: TargetingPresetKeys.CUSTOM_17,
      allowKey: TargetingPresetKeys.CUSTOM_17_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.CUSTOM_17,
      allowKey: TargetingPresetKeys.CUSTOM_17_INCLUSION,
    } as IIncludeExcludeStringProps,
  },
  Custom18: {
    key: TargetingPresetParamKeys.CUSTOM_18,
    label: 'Custom 18',
    categoryKey: TargetingPresetCategoryKeys.CUSTOMS,
    selectCategoryKey: 'Custom',
    apiKeys: [TargetingPresetKeys.CUSTOM_18, TargetingPresetKeys.CUSTOM_18_INCLUSION],
    customValidation: {
      validateKey: TargetingPresetKeys.CUSTOM_18,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CUSTOM_18,
      placeholder: generateCustomPlaceholder('Custom 18'),
      valueKey: TargetingPresetKeys.CUSTOM_18,
      allowKey: TargetingPresetKeys.CUSTOM_18_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.CUSTOM_18,
      allowKey: TargetingPresetKeys.CUSTOM_18_INCLUSION,
    } as IIncludeExcludeStringProps,
  },
  CustomTargeting: {
    key: TargetingPresetParamKeys.CUSTOM_TARGETING,
    label: 'Custom Targeting',
    categoryKey: TargetingPresetCategoryKeys.CUSTOMS,
    selectCategoryKey: 'Custom',
    apiKeys: [TargetingPresetKeys.CUSTOM_TARGETING, TargetingPresetKeys.CUSTOM_TARGETING_INCLUSION],
    customValidation: {
      validateKey: TargetingPresetKeys.CUSTOM_TARGETING,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CUSTOM_TARGETING,
      placeholder: generateCustomPlaceholder('Custom Targeting'),
      valueKey: TargetingPresetKeys.CUSTOM_TARGETING,
      allowKey: TargetingPresetKeys.CUSTOM_TARGETING_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
    SummaryComponent: IncludeExcludeString,
    summaryProps: {
      valueKey: TargetingPresetKeys.CUSTOM_TARGETING,
      allowKey: TargetingPresetKeys.CUSTOM_TARGETING_INCLUSION,
    } as IIncludeExcludeStringProps,
  },
};

export interface ITargetingPresetCategory {
  key: TargetingPresetCategory; // also acts as label
  paramKeys: TargetingPresetParamKey[];
  iconName: string;
  subText: string;
  addParamText: string;
}

export const targetingPresetCategoriesAndParameters: Record<
  TargetingPresetCategory,
  ITargetingPresetCategory
> = {
  Supply: {
    key: 'Supply',
    paramKeys: [
      'TargetedDevices',
      'TargetedDeviceTypes',
      'TargetedDeviceSubtypes',
      'IPFilter',
      'ContentChannel',
      'ContentDistributor',
      'ContentGenre',
      'ContentID',
      'ContentNetwork',
      'ContentProducer',
      'ContentRating',
      'ContentSeries',
      'ContentTitle',
      'ContentCategory',
      'TargetedLivestreams',
      'AppBundleIDs',
      'SitePages',
      'SiteDomains',
    ],
    iconName: 'cast',
    subText:
      'All Devices, All Content Params, All Stream, All Apps , All Sites Pages , All Site Domains',
    addParamText: '+ Add Supply Parameter',
  },
  Users: {
    key: 'Users',
    paramKeys: [
      'TargetedRegions', // COUNTRY GROUPS
      'TargetedCountries',
      'LocalTargetingAllowed',
      'LocalTargetingDisallowed',
      'Dayparting',
      'AudienceSegments',
      'ContextualSegments',
      'COPPA',
      'LimitedAdTracking',
      'CCPA',
      'GDPR',
    ],
    iconName: 'eye',
    subText: ' All Geos, All Time (Dayparting), All Segments, Privacy',
    addParamText: '+ Add User Parameter',
  },

  Customs: {
    key: 'Customs',
    paramKeys: [
      'Custom4',
      'Custom5',
      'Custom6',
      'Custom7',
      'Custom8',
      'Custom9',
      'Custom10',
      'Custom11',
      'Custom12',
      'Custom13',
      'Custom14',
      'Custom15',
      'Custom16',
      'Custom17',
      'Custom18',
      'CustomTargeting',
    ],
    iconName: 'code',
    subText: 'All Custom Settings',
    addParamText: '+ Add Custom Parameter',
  },
};
export interface IParameterDropdown {
  key: string;
  getList: (session: ISessionState, user: IUserState) => Promise<ISelectOption[]>;
}

export const targetingPresetDropdowns = {
  Countries: {
    key: 'Countries',
    getList: () => generateOptionsListAsPromise(generateCountryOptions()),
  },
  CountryGroups: {
    key: 'CountryGroups',
    getList: generateCountryGroupOptionsList,
  },
  DMAs: {
    key: 'DMAs',
    getList: () => generateOptionsListAsPromise(generateDMAOptions('us')),
  },
  COPPA: {
    key: 'COPPA',
    getList: () => generateOptionsListAsPromise(TargetingCOPPAOptions),
  },
  LimitedAdTracking: {
    key: 'LimitedAdTracking',
    getList: () => generateOptionsListAsPromise(TargetBoolOptions),
  },
  CCPA: {
    key: 'CCPA',
    getList: () => generateOptionsListAsPromise(TargetingCCPAOptions),
  },
  GDPR: {
    key: 'GDPR',
    getList: () => generateOptionsListAsPromise(TargetingGDPROptions),
  },
  GDPRConsent: {
    key: 'GDPRConsent',
    getList: () => generateOptionsListAsPromise(TargetingGDPRConsentOptions),
  },
  Devices: {
    key: 'Devices',
    getList: () => generateOptionsListAsPromise(DeviceOptions),
  },
  DeviceTypes: {
    key: 'DeviceTypes',
    getList: () => generateOptionsListAsPromise(DeviceORTBTypeOptions),
  },
  DeviceSubTypes: {
    key: 'DeviceSubTypes',
    getList: generateDeviceSubTypeOptionsList,
  },
  Livestreams: {
    key: 'Livestreams',
    getList: () => generateOptionsListAsPromise(TargetingLiveStreamOptions),
  },
  AudienceSegments: {
    key: 'AudienceSegments',
    getList: generateAudienceSegmentOptionsList,
  },
  ContextualSegments: {
    key: 'ContextualSegments',
    getList: generateContextualSegmentOptionsList,
  },
} as Record<string, IParameterDropdown>;

export interface ITargetingPresetAssociatedBadge {
  dataKey: keyof ITargetingPreset;
  singularLabel: string;
  pluralLabel: ItemRouteType;
  color: string;
  iconName: string;
  modalTitle: string;
}

export interface ITargetingPresetFormConfig {
  badges: ITargetingPresetAssociatedBadge[];
  categories: Record<TargetingPresetCategory, ITargetingPresetCategory>;
  params: Record<TargetingPresetParamKey, ITargetingPresetParam>;
  dropdowns: Record<string, IParameterDropdown>;
}

export const targetingPresetFormConfig: ITargetingPresetFormConfig = {
  badges: [
    {
      dataKey: 'Bidders',
      singularLabel: 'Bidder',
      pluralLabel: 'Bidders',
      color: publicaColors.honeydew,
      iconName: 'bidders',
      modalTitle: 'Associated Bidders',
    },
    {
      dataKey: 'Campaigns',
      singularLabel: 'Campaign',
      pluralLabel: 'Campaigns',
      color: publicaColors.lavender,
      iconName: 'paper-plane',
      modalTitle: 'Associated Campaigns',
    },
  ],
  categories: targetingPresetCategoriesAndParameters,
  params: TargetingPresetParameters,
  dropdowns: targetingPresetDropdowns,
};

export const generateTargetingPresetParameterOptions = (
  optionKeys: TargetingPresetParamKey[],
  publisherID: string,
  isSuperUser: boolean,
) => {
  const options: any[] = [];
  let currentCategory = '' as TargetingPresetSelectCategory;
  optionKeys.forEach((key) => {
    const { label, selectCategoryKey, isSuperUserOnly, checkIfHidden } =
      TargetingPresetParameters[key];
    if (selectCategoryKey !== currentCategory) {
      currentCategory = selectCategoryKey;
      options.push({
        key: selectCategoryKey,
        label: selectCategoryKey,
        isCategory: true,
        category: selectCategoryKey,
      });
    }
    if (isSuperUserOnly && !isSuperUser) return;
    if (checkIfHidden && checkIfHidden(publisherID, isSuperUser)) return;
    options.push({
      key,
      label,
      category: selectCategoryKey,
    });
  });
  return options;
};

export const emptyActiveParametersShell = {
  Supply: [],
  Users: [],
  Customs: [],
} as Record<TargetingPresetCategory, TargetingPresetParamKey[]>;

export const extractSuperUserOnlyTargetingPresetParamKeys = (): TargetingPresetParamKey[] => {
  const result: TargetingPresetParamKey[] = [];
  Object.values(TargetingPresetParameters).forEach((param) => {
    if (param.isSuperUserOnly) {
      result.push(param.key);
    }
  });
  return result;
};
